import type { ManifestSlide } from '@/types/videoManifest';

export interface TimelineBlock {
  id: string;
  title: string;
  /** How long the scene plays, or null when nothing knows yet. */
  seconds: number | null;
  /** Its share of the timeline's width. */
  weight: number;
  /** Where it sits in the video; only scenes the manifest has. */
  start?: number;
  end?: number;
}

export type TimedSlide = Pick<ManifestSlide, 'id' | 'duration' | 'startTime' | 'endTime'> & { transitionOut?: { duration: number } };

export function placesInTime(manifest: { slides: TimedSlide[] } | null) {
  const places = new Map<string, { start: number; end: number }>();
  let cursor = 0;
  for (const s of manifest?.slides ?? []) {
    const start = s.startTime ?? cursor;
    const end = s.endTime ?? start + s.duration;
    places.set(s.id, { start, end });
    cursor = end + (s.transitionOut?.duration ?? 0);
  }
  return places;
}

export function timelineBlocks(slides: Array<{ id: string; title: string; duration: number }>, manifest: { slides: TimedSlide[] } | null): TimelineBlock[] {
  const places = placesInTime(manifest);
  const known = slides.map((s) => {
    const place = places.get(s.id);
    const seconds = place ? place.end - place.start : s.duration > 0 ? s.duration : null;
    return { id: s.id, title: s.title, seconds, ...place };
  });
  const lengths = known.flatMap((b) => (b.seconds == null ? [] : [b.seconds]));
  const average = lengths.length ? lengths.reduce((a, b) => a + b, 0) / lengths.length : 1;
  return known.map((b) => ({ ...b, weight: b.seconds ?? average }));
}

export function playheadAt(blocks: TimelineBlock[], time: number): { id: string; fraction: number } | null {
  const placed = blocks.filter((b) => b.start != null && b.end != null);
  if (!placed.length) return null;
  let at = { id: placed[0].id, fraction: 0 };
  for (const b of placed) {
    if (time < b.start!) break;
    const length = b.end! - b.start!;
    at = { id: b.id, fraction: length > 0 ? Math.min(1, (time - b.start!) / length) : 1 };
  }
  return at;
}

export function timeAt(block: TimelineBlock, fraction: number): number | null {
  if (block.start == null || block.end == null) return null;
  return block.start + Math.min(1, Math.max(0, fraction)) * (block.end - block.start);
}
